import { DatePicker } from "antd";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ClockCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import TimeInput from "../../../common/TimeInput";
import { EditSessionModalContext } from "./types";

const formatWithLeadingZero = (num: number) =>
  num < 10 ? "0" + num : num.toString();

const CalendarTimeInput: React.FC = () => {
  const { startTime, setStartTime, endTime, setEndTime, day, setDay } =
    useContext(EditSessionModalContext);

  const [intervalDuration, setIntervalDuration] = useState<string>("0:30");
  const showPlusOneBadge = useMemo<boolean>(() => {
    return moment(endTime, "HH:mm").isBefore(moment(startTime, "HH:mm"));
  }, [startTime, endTime]);

  const recalculateIntervalDuration = (start: string, end: string) => {
    const [startHour, startMinute] = start.split(":");
    const [endHour, endMinute] = end.split(":");
    let hoursDuration = parseInt(endHour) - parseInt(startHour);
    let minutesDuration = parseInt(endMinute) - parseInt(startMinute);

    if (isNaN(minutesDuration)) minutesDuration = 0;
    if (isNaN(hoursDuration)) hoursDuration = 0;
    if (hoursDuration < 0) hoursDuration = 24 + hoursDuration;
    if (minutesDuration < 0) {
      minutesDuration = 60 + minutesDuration;
      if (hoursDuration === 0) hoursDuration = 23;
      else hoursDuration--;
    }

    setIntervalDuration(
      formatWithLeadingZero(hoursDuration) +
        ":" +
        formatWithLeadingZero(minutesDuration),
    );
  };

  const handleDurationBlur = (duration?: string) => {
    const [hours, minutes] = duration?.split(":") || ["0", "0"];
    const newEndTime = moment(startTime, "HH:mm")
      .add(parseInt(hours), "hours")
      .add(parseInt(minutes), "minutes")
      .format("HH:mm");
    setEndTime(newEndTime);
  };
  const handleDurationChange = (value: string) => {
    setIntervalDuration(value);
  };

  const getDisabledDate = (currentDate: moment.Moment) => {
    const now = moment();
    return (
      currentDate &&
      (currentDate > now.endOf("day") || currentDate.month() !== now.month())
    );
  };

  const handleStartBlur = (time?: string) => {
    setStartTime(time || "");
  };

  const handleEndBlur = (time?: string) => {
    setEndTime(time || "");
  };

  useEffect(() => {
    recalculateIntervalDuration(startTime, endTime);
  }, [startTime, endTime]);

  return (
    <div
      style={{
        background: "#f8f9fb",
        borderRadius: 8,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {/* Time row: start – end | duration */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ClockCircleOutlined style={{ color: "#8c8c8c", fontSize: 14 }} />
        <div style={{ position: "relative" }}>
          <TimeInput
            placeholder="Start"
            style={{
              width: 72,
              textAlign: "center",
              borderRadius: 6,
              border: "1px solid #e0e0e0",
              background: "#fff",
            }}
            onBlur={handleStartBlur}
            valueProps={startTime}
          />
        </div>
        <span style={{ color: "#bbb", fontSize: 14 }}>–</span>
        <div style={{ position: "relative" }}>
          <TimeInput
            style={{
              width: 72,
              textAlign: "center",
              borderRadius: 6,
              border: "1px solid #e0e0e0",
              background: "#fff",
            }}
            placeholder="End"
            onBlur={handleEndBlur}
            valueProps={endTime}
          />
          {showPlusOneBadge && (
            <span
              style={{
                position: "absolute",
                top: -6,
                right: -6,
                color: "#fff",
                background: "#ff4d4f",
                borderRadius: "50%",
                fontSize: 10,
                fontWeight: 600,
                width: 18,
                height: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              +1
            </span>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            background: "#eef1f5",
            borderRadius: 6,
            padding: "4px 10px",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span
            style={{ color: "#8c8c8c", fontSize: 12, whiteSpace: "nowrap" }}
          >
            Duration:
          </span>
          <TimeInput
            onBlur={handleDurationBlur}
            onChange={handleDurationChange}
            valueProps={intervalDuration}
            style={{
              width: 48,
              textAlign: "center",
              border: "none",
              background: "transparent",
              fontWeight: 600,
              fontSize: 13,
              padding: 0,
            }}
          />
        </div>
      </div>

      {/* Date row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CalendarOutlined style={{ color: "#8c8c8c", fontSize: 14 }} />
        <DatePicker
          style={{ flex: 1, borderRadius: 6 }}
          defaultPickerValue={moment()}
          value={day ? moment(day) : null}
          defaultValue={moment()}
          onSelect={(value) => {
            setDay(value);
          }}
          disabledDate={getDisabledDate}
        />
      </div>
    </div>
  );
};
export default CalendarTimeInput;
